import axios from 'axios';
import jwtDecode from 'jwt-decode';
import { API_URL, DATE_KEYS } from 'utils/constants';
import { getIsDevelopment } from 'constants/environment';
import { formatDateDB } from 'utils/timeProvider';
import {
  destroyLocalAuth,
  getLocalAuth,
  isTokenExpired,
  isTokenOld,
  setLocalAuth,
} from './auth';
import DatadogErrorBoundary from 'components/Atoms/DatadogErrorBoundary';
import {
  trackNetworkError,
  startApiResourceTracking,
  completeApiResourceTracking,
} from 'utils/affordaPortalTracking';
import {
  classifyAffordaPortalError,
  createAffordaPortalError,
  extractAffordaPortalErrorMessage,
  toReportableError,
} from 'utils/affordaPortalErrors';

// In browser/axios responses, a status/code of 0 typically indicates a network error (e.g. CORS, offline).
const isErrorCode = (code) => code >= 400 || code === 0;

export const throwIfError = (response) => {
  if (
    isErrorCode(response?.status) ||
    isErrorCode(response?.error?.code) ||
    isErrorCode(response?.error?.statusCode) ||
    response?.type?.startsWith('error-')
  ) {
    const normalizedError = createAffordaPortalError(
      response?.error || response,
      {
        ...(response?.classification || {}),
        hasResponse: response?.type === 'error-response',
        rawMessage:
          response?.classification?.rawMessage ||
          extractAffordaPortalErrorMessage(response),
        statusCode: response?.status,
      },
    );

    if (getIsDevelopment()) {
      console.error(normalizedError.message, {
        classification: normalizedError.affordaPortal,
        response,
      });
    }
    DatadogErrorBoundary.reportError(toReportableError(normalizedError), {
      source: 'api-success-error',
      status: response?.status,
      method: response?.request?.method,
      path: response?.request?.path,
      errorCode: normalizedError.code,
      errorMessage: normalizedError.affordaPortal?.rawMessage,
    });

    throw normalizedError;
  }

  return response;
};

export const handleRequestAndThrowIfError = async (request) => {
  const response = await request;
  throwIfError(response);
  return response;
};

const requestAPI = async (url, data, options) => {
  let { authToken } = getLocalAuth();
  if (authToken && !authToken.startsWith('Bearer')) {
    authToken = `Bearer ${authToken}`;
  }

  const requestOptions = {
    headers: {
      Authorization: authToken,
      'Content-Type': 'application/json',
    },
    data: undefined,
  };
  if (data) {
    if (data instanceof FormData) {
      data.append('client', 'portal');

      requestOptions.data = data;
    } else {
      requestOptions.data = { ...data, client: 'portal' };

      // format dates defined in [DATE_KEYS] to DB friendly format [YYYY-MM-DD]
      if (Object.keys(requestOptions.data).includes(DATE_KEYS)) {
        Object.keys(requestOptions.data).forEach((key) => {
          if (key?.includes(DATE_KEYS) && requestOptions?.data[key]) {
            requestOptions.data[key] = formatDateDB(requestOptions.data[key]);
          }
        });
      }
    }
  }

  const method = options?.method || 'get';
  const requestSummary = { method, path: url.split('?')[0] };

  // Start API resource tracking
  const trackingContext = startApiResourceTracking(url, method);

  try {
    await checkSession();

    try {
      const response = await axios.request({
        url: `${API_URL}${url}`,
        ...requestOptions,
        ...options,
      });

      return response.data;
    } catch (error) {
      const duration = Date.now() - trackingContext.startTime;
      const { response, request } = error;
      const classifiedError = classifyAffordaPortalError(error);
      const status =
        classifiedError.statusCode ??
        response?.status ??
        request?.status ??
        500;

      // Enhanced error tracking with full context
      trackNetworkError(error, url, method, {
        duration,
        statusCode: status,

        // Sanitized request info (don't send actual data)
        hasRequestData: Boolean(data),
        requestDataKeys:
          data && typeof data === 'object' && !(data instanceof FormData)
            ? Object.keys(data).filter((k) => k !== 'client')
            : data instanceof FormData
              ? ['FormData']
              : [],

        // Error classification
        errorCategory: classifiedError.category,
        errorReason: classifiedError.reason,
        errorMessageId: classifiedError.userMessageId,
        isConnectivityIssue: classifiedError.isConnectivityIssue,
        isTimeout: classifiedError.isTimeout,

        // Additional debugging info
        apiUrl: API_URL,
        hasAuthToken: Boolean(authToken),
      });

      // Complete failed tracking
      completeApiResourceTracking(trackingContext, false, {
        statusCode: status,
        errorType: classifiedError.reason || error.code || 'unknown',
        errorMessage: classifiedError.rawMessage || error.message,
        hasResponse: Boolean(response),
      });

      /**
       * Bad Request / Error Response
       */
      if (response) {
        return {
          error: response.data,
          type: 'error-response',
          status,
          classification: classifiedError,
          request: requestSummary,
        };
      }
      /**
       * No Response
       */
      if (request) {
        return {
          error,
          type: 'error-request',
          status,
          classification: classifiedError,
          request: requestSummary,
        };
      }
      /**
       * Catch-All
       */
      return {
        error,
        type: 'error-unknown',
        classification: classifiedError,
        request: requestSummary,
      };
    }
  } catch (error) {
    // Authentication / Session errors
    trackNetworkError(error, url, method, {
      errorCategory: 'authentication',
      isSessionError: true,
    });

    /**
     * Authentication / Invalid Token
     *   * Destroy LocalStorage
     *   * Hard Refresh destroys Contexts
     */
    console.warn(`Encountered Authentication Issue: ${error.message}`, error);
    destroyLocalAuth();
    window.location.reload();

    return true;
  }
};

const checkSession = async () => {
  const { authToken } = getLocalAuth();

  if (authToken) {
    const isExpired = isTokenExpired(authToken);
    const isOld = isTokenOld(authToken);

    if (isExpired) {
      throw Error('Authentication Token has expired');
    }

    if (isOld) {
      await extendSession(authToken);
    }
  }

  return true;
};

export const extendSession = async (authToken) => {
  const response = await axios.get(`${API_URL}/portal/auth/extendSession`, {
    headers: {
      Authorization: authToken,
      'Content-Type': 'application/json',
    },
  });

  setLocalAuth(response.data);
};

export const regenerateTokenIfCustomerIdMissing = async () => {
  const { authToken } = getLocalAuth();
  const decoded = jwtDecode(authToken);

  if (!decoded.customerId) {
    await extendSession(authToken);
  }
};

export const get = (url, options) =>
  requestAPI(url, null, { method: 'get', ...options });
export const post = (url, data, options) =>
  requestAPI(url, data, { method: 'post', ...options });
export const put = (url, data, options) =>
  requestAPI(url, data, { method: 'put', ...options });
export const del = (url, options) =>
  requestAPI(url, null, { method: 'delete', ...options });
